'use client';

import type { ComponentProps, ReactElement, ReactNode } from 'react';

import {
  Tooltip as TooltipRoot,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

export type TooltipProps = Pick<
  ComponentProps<typeof TooltipRoot>,
  'open' | 'defaultOpen' | 'onOpenChange' | 'delayDuration'
> & {
  /**
   * Figma "Text" - a short, non-essential hint. One line, or wraps at 240px
   * (Figma Length=Long; keep it to 2–3 lines). Never interactive content.
   */
  content: ReactNode;
  /** Figma "Placement": which side of the trigger the tooltip sits on. */
  side?: 'top' | 'bottom' | 'left' | 'right';
  /** Gap between the trigger and the arrow tip. */
  sideOffset?: number;
  /** Class for the tooltip body. */
  className?: string;
  /** The trigger: one focusable element (a Button, an IconButton, a link). */
  children: ReactElement;
};

/**
 * Hint on hover and keyboard focus (Figma: Components ↳ Controls ↳ Tooltip).
 * Icon Buttons pair their `aria-label` with a Tooltip showing the same text.
 * Opens after 400ms, closes at once, Escape dismisses. Needs a
 * `TooltipProvider` above it - wrap the app once in the root layout.
 */
export function Tooltip({
  content,
  side = 'top',
  sideOffset,
  className,
  children,
  ...rootProps
}: TooltipProps) {
  return (
    <TooltipRoot {...rootProps}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side={side} sideOffset={sideOffset} className={className}>
        {content}
      </TooltipContent>
    </TooltipRoot>
  );
}

export { TooltipProvider };
